import { useEffect, useRef } from 'react';

import type { ThemeMode } from '@/types/theme';

const DOT_SIZE = 150.0;

const COLORS: Record<
  'dark' | 'light',
  { a: [number, number, number]; b: [number, number, number] }
> = {
  dark: {
    a: [0x55 / 255, 0xde / 255, 0xe0 / 255],
    b: [0xb3 / 255, 0xd8 / 255, 0xff / 255]
  },
  light: {
    a: [0x06 / 255, 0x47 / 255, 0x62 / 255],
    b: [0x06 / 255, 0x47 / 255, 0x62 / 255]
  }
};

// Colors passed as uniforms so theme changes don't require a WebGL context rebuild
const VS = `
  attribute vec3 aPosition;
  attribute float aSize;
  uniform float uTime;
  uniform float uPixelRatio;
  uniform float uDotSize;
  uniform vec2  uMouse;
  uniform vec3  uColorA;
  uniform vec3  uColorB;
  uniform float uAlphaScale;
  uniform mat4  uMV;
  uniform mat4  uProj;
  varying vec3  vColor;
  varying float vAlpha;

  void main() {
    vec3 p = aPosition;
    float u = (p.x + 130.0) / 260.0;
    float v = (p.z + 35.0)  / 70.0;

    vColor = mix(uColorA, uColorB, u * 0.85);

    float mxPhase = uMouse.x * 2.8;
    float myAmp   = 1.0 + uMouse.y * 0.001;

    p.y += sin(u * 34.0 + uTime * 1.25 + mxPhase)           * 6.6 * myAmp;
    p.y += sin(u * 18.0 - uTime * 1.55 + v * 9.0 + mxPhase) * 5.1 * myAmp;
    p.y += cos(u * 10.0 + uTime * 0.85 + mxPhase * 0.5)     * 2.6 * myAmp;
    p.x += sin(uTime * 0.9 + v * 8.0 + uMouse.x * 1.5)      * 1.8;

    vec4 mvPos = uMV * vec4(p, 1.0);
    float center = 1.0 - clamp(abs(u - 0.56) / 0.56, 0.0, 1.0);
    vAlpha = clamp((0.22 + center * 0.72) * uAlphaScale, 0.0, 1.0);
    gl_PointSize = aSize * uPixelRatio * (uDotSize / -mvPos.z);
    gl_Position  = uProj * mvPos;
  }
`;

const FS = `
  precision mediump float;
  varying vec3  vColor;
  varying float vAlpha;
  void main() {
    float disc = smoothstep(0.5, 0.18, length(gl_PointCoord - 0.5));
    gl_FragColor = vec4(vColor, disc * vAlpha);
  }
`;

function compileShader(gl: WebGLRenderingContext, type: number, src: string) {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS))
    throw new Error(gl.getShaderInfoLog(s) ?? '');
  return s;
}

function mul(a: Float32Array, b: Float32Array) {
  const o = new Float32Array(16);
  for (let r = 0; r < 4; r++)
    for (let c = 0; c < 4; c++)
      for (let k = 0; k < 4; k++) o[r + c * 4] += a[r + k * 4] * b[k + c * 4];
  return o;
}

function perspective(
  fovDeg: number,
  aspect: number,
  near: number,
  far: number
) {
  const f = 1 / Math.tan((fovDeg * Math.PI) / 360);
  const d = near - far;
  return new Float32Array([
    f / aspect,
    0,
    0,
    0,
    0,
    f,
    0,
    0,
    0,
    0,
    (far + near) / d,
    -1,
    0,
    0,
    (2 * far * near) / d,
    0
  ]);
}

const rotX = (r: number) => {
  const c = Math.cos(r),
    s = Math.sin(r);
  return new Float32Array([1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]);
};
const rotZ = (r: number) => {
  const c = Math.cos(r),
    s = Math.sin(r);
  return new Float32Array([c, s, 0, 0, -s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
};
const trans = (x: number, y: number, z: number) =>
  new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1]);
const scaleM = (x: number, y: number, z: number) =>
  new Float32Array([x, 0, 0, 0, 0, y, 0, 0, 0, 0, z, 0, 0, 0, 0, 1]);

interface Props {
  themeMode: ThemeMode;
}

const WaveBackground = ({ themeMode }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const themeModeRef = useRef(themeMode);

  // Keep ref in sync without restarting the WebGL loop
  useEffect(() => {
    themeModeRef.current = themeMode;
  }, [themeMode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      premultipliedAlpha: false
    });
    if (!gl) return;

    try {
      const prog = gl.createProgram()!;
      gl.attachShader(prog, compileShader(gl, gl.VERTEX_SHADER, VS));
      gl.attachShader(prog, compileShader(gl, gl.FRAGMENT_SHADER, FS));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
        const err = gl.getProgramInfoLog(prog) ?? 'Unknown link error';
        throw new Error(err);
      }
      // biome-ignore lint/correctness/useHookAtTopLevel: gl.useProgram is a WebGL call, not a React hook
      gl.useProgram(prog);

      const aPos = gl.getAttribLocation(prog, 'aPosition');
      const aSz = gl.getAttribLocation(prog, 'aSize');
      const uTime = gl.getUniformLocation(prog, 'uTime');
      const uPR = gl.getUniformLocation(prog, 'uPixelRatio');
      const uDotSize = gl.getUniformLocation(prog, 'uDotSize');
      const uMouse = gl.getUniformLocation(prog, 'uMouse');
      const uColorA = gl.getUniformLocation(prog, 'uColorA');
      const uColorB = gl.getUniformLocation(prog, 'uColorB');
      const uAlphaScale = gl.getUniformLocation(prog, 'uAlphaScale');
      const uMV = gl.getUniformLocation(prog, 'uMV');
      const uProj = gl.getUniformLocation(prog, 'uProj');

      const COLS = 200,
        ROWS = 75,
        COUNT = COLS * ROWS;
      const positions = new Float32Array(COUNT * 3);
      const sizes = new Float32Array(COUNT);

      for (let z = 0; z < ROWS; z++) {
        for (let x = 0; x < COLS; x++) {
          const i = z * COLS + x;
          const u = x / (COLS - 1);
          const v = z / (ROWS - 1);
          positions[i * 3 + 0] = (u - 0.5) * 260;
          positions[i * 3 + 1] = 0;
          positions[i * 3 + 2] = (v - 0.5) * 70;
          const center = 1 - Math.abs(u - 0.55) / 0.55;
          sizes[i] = 1.1 + 2.7 * center * center;
        }
      }

      const posBuffer = gl.createBuffer()!;
      gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 0, 0);

      const szBuffer = gl.createBuffer()!;
      gl.bindBuffer(gl.ARRAY_BUFFER, szBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, sizes, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(aSz);
      gl.vertexAttribPointer(aSz, 1, gl.FLOAT, false, 0, 0);

      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.disable(gl.DEPTH_TEST);

      let W = 0,
        H = 0;
      let proj = new Float32Array(16);

      const resize = () => {
        const pr = Math.min(devicePixelRatio, 2);
        W = Math.floor(canvas.offsetWidth * pr);
        H = Math.floor(canvas.offsetHeight * pr);
        canvas.width = W;
        canvas.height = H;
        gl.viewport(0, 0, W, H);
        // Recompute proj only on resize, not every frame
        proj = perspective(48, W / H, 0.1, 1000);
      };
      resize();
      window.addEventListener('resize', resize);

      // Precompute constant matrices outside the animation loop
      const view = mul(rotX(0.17), trans(0, -12, -95));
      const baseModel = mul(
        trans(0, -10, 0),
        mul(rotX(-0.48), scaleM(1.8, 1.35, 1.12))
      );

      const mouseTarget = [0, 0];
      const mouseCurrent = [0, 0];
      const onMouseMove = (e: MouseEvent) => {
        mouseTarget[0] = (e.clientX / window.innerWidth) * 2 - 1;
        mouseTarget[1] = (e.clientY / window.innerHeight) * 2 - 1;
      };
      window.addEventListener('mousemove', onMouseMove);

      let rafId = 0;
      const frame = (ms: number) => {
        const t = ms * 0.001;
        const pr = Math.min(devicePixelRatio, 2);

        mouseCurrent[0] += (mouseTarget[0] - mouseCurrent[0]) * 0.05;
        mouseCurrent[1] += (mouseTarget[1] - mouseCurrent[1]) * 0.05;

        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        const isLight = themeModeRef.current === 'light';
        const { a, b } = COLORS[isLight ? 'light' : 'dark'];

        // Only compute time-varying rotation per frame
        const model = mul(baseModel, rotZ(Math.sin(t * 0.25) * 0.025));

        gl.uniformMatrix4fv(uMV, false, mul(view, model));
        gl.uniformMatrix4fv(uProj, false, proj);
        gl.uniform1f(uTime, t);
        gl.uniform1f(uPR, pr);
        gl.uniform1f(uDotSize, DOT_SIZE);
        gl.uniform2f(uMouse, mouseCurrent[0], -mouseCurrent[1]);
        gl.uniform3fv(uColorA, a);
        gl.uniform3fv(uColorB, b);
        gl.uniform1f(uAlphaScale, isLight ? 2.5 : 1.0);

        gl.drawArrays(gl.POINTS, 0, COUNT);
        rafId = requestAnimationFrame(frame);
      };
      rafId = requestAnimationFrame(frame);

      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener('resize', resize);
        window.removeEventListener('mousemove', onMouseMove);
      };
    } catch (err) {
      console.error(
        '[WaveBackground] Shader compilation/linking failed, falling back to CSS gradient:',
        err
      );
      return;
    }
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        left: 0,
        bottom: 0,
        width: '100%',
        height: '60vh',
        pointerEvents: 'none',
        zIndex: 1,
        display: 'block'
      }}
    />
  );
};

export default WaveBackground;
